import { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import clsx from 'clsx';

import type { CarBrandList, CarModelList, CarSubTypeList, CarTypeList } from 'entities/car';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from 'shared/api';
import ArrowRight from 'shared/assets/icons/arrow_right.svg';
import { ButtonCustom } from 'shared/ui/button-custom';
import { DownloadButton } from 'shared/ui/download-button';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import { EditApplicationModal } from './edit-application-modal';
import styles from './open-application.module.scss';

export const OpenApplication = () => {
  const { type, uid } = useParams();
  const navigate = useNavigate();

  const [appData, setAppData] = useState<any>([]);
  const [rowsSelect, setRowsSelect] = useState<any>([]);
  const [rowsLeasing, setRowsLeasing] = useState<any>([]);
  const [dynamicField, setDynamicField] = useState<any>([]);

  // Состояние для редактирования
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Справочники
  const [typeValueList, setTypeValueList] = useState<CarTypeList>([]);
  const [subTypeValueList, setSubTypeValueList] = useState<CarSubTypeList>([]);
  const [brandValueList, setBrandValueList] = useState<CarBrandList>([]);
  const [modelValueList, setModelValueList] = useState<CarModelList>([]);

  const postData = async () => {
    try {
      // eslint-disable-next-line no-console
      console.log({ type, uid });
      const data = { id: uid };
      const url = type == 'select' ? '/select-applications/app' : '/leasing-applications/app';
      const response = await apiClient.post(url, data);
      setAppData(response?.data?.data?.application);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при отправке данных:', error);
    }
  };

  // Функции для загрузки справочников
  const fetchCarTypesData = async () => {
    try {
      const response1 = await apiClient.get('/car-types/all');
      const data1 = await response1;
      setTypeValueList([
        ...(data1.data.data as CarTypeList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);
    } catch (error) {
      setTypeValueList([{ id: 0, name: 'Иное', value: 'other' }]);
      // eslint-disable-next-line no-console
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getBrandsForType = async (carTypeId: number | string) => {
    try {
      const response = await apiClient.post('/car-brands/type', { typeId: carTypeId });
      const data = await response;
      setBrandValueList([
        ...(data.data.data as CarBrandList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getSubtypeForType = async (carTypeId: number | string) => {
    try {
      const response = await apiClient.post('/car-subtypes/subtypes', {
        typeId: carTypeId,
      });
      const data = await response;
      setSubTypeValueList([
        ...(data.data.data as CarSubTypeList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getBrandsForSubType = async (carSubtypeId: number | string) => {
    try {
      const response = await apiClient.post('/car-brands/subtype', { subtypeId: carSubtypeId });
      const data = await response;
      setBrandValueList([
        ...(data.data.data as CarBrandList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getVehicleForBrandAndSubType = async (brand: number | string, subtype: number | string) => {
    try {
      const response = await apiClient.post('/vehicles/brandSubType', {
        brandId: brand,
        subtypeId: subtype,
      });
      const data = await response;
      // eslint-disable-next-line camelcase
      const otherOption = { id: 0, name: 'Иное', car_params: [], value: 'other' };
      setModelValueList([...(data.data.data as CarModelList), otherOption]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  // Загружаем справочники при монтировании компонента
  useEffect(() => {
    fetchCarTypesData();
  }, []);

  useEffect(() => {
    if (uid) {
      postData();
    }
  }, [uid]);

  useEffect(() => {
    setDynamicField(appData?.car_params || []);
    if (type == 'select') {
      setRowsSelect(appData?.select_offers || []);
    } else {
      setRowsLeasing(appData?.leasing_offers || []);
    }
  }, [appData]);

  const renderApp = () => {
    if (type == 'select') {
      return (
        <Box className={styles.appWrapper}>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Статус заявки</span>
            <span className={styles.appDescValue}>
              {appData?.fulfilled === false ? 'Черновик' : appData?.status_app}
            </span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Состояние</span>
            <span className={styles.appDescValue}>{appData?.condition}</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Место поставки ТС</span>
            <span className={styles.appDescValue}>{appData?.destination}</span>
          </Box>
          {appData?.currency && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Валюта</span>
              <span className={styles.appDescValue}>{appData?.currency}</span>
            </Box>
          )}
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Cпособ покупки</span>
            <span className={styles.appDescValue}>{appData?.pay_method}</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Количество ТС</span>
            <span className={styles.appDescValue}>{appData?.quantity}</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Траснспортное средство</span>
            <span className={styles.appDescValue}>
              {appData?.car_type ? `${appData?.car_type} - ` : ''}
              {appData?.car_subtype ? appData?.car_subtype : ''}
            </span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Марка</span>
            <span className={styles.appDescValue}>{appData?.car_brand}</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Модель</span>
            <span className={styles.appDescValue}>{appData?.vehicle}</span>
          </Box>
          {dynamicField?.map((field: any) => (
            <Box key={field?.fieldName} className={styles.appDesc}>
              <span className={styles.appDescTitle}>{field?.fieldName}</span>
              <span className={styles.appDescValue}>
                {Array.isArray(field?.fieldValue) ? field.fieldValue.join(', ') : field?.fieldValue}
              </span>
            </Box>
          ))}
          {appData?.inn && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ИНН</span>
              <span className={styles.appDescValue}>{appData?.inn}</span>
            </Box>
          )}
          {appData?.phone && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Телефон</span>
              <span className={styles.appDescValue}>{appData?.phone}</span>
            </Box>
          )}
          {appData?.email && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>E-mail</span>
              <span className={styles.appDescValue}>{appData?.email}</span>
            </Box>
          )}
          {appData?.bio && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ФИО</span>
              <span className={styles.appDescValue}>{appData?.bio}</span>
            </Box>
          )}
          {appData?.kp && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Имеющееся предложение</span>
              <DownloadButton fileUrl={appData?.kp?.url} title={appData?.kp?.name} />
            </Box>
          )}
        </Box>
      );
    }
    if (type == 'leasing') {
      return (
        <Box className={styles.appWrapper}>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Статус заявки</span>
            <span className={styles.appDescValue}>
              {appData?.fulfilled === false ? 'Черновик' : appData?.status_app}
            </span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Траснспортное средство</span>
            <span className={styles.appDescValue}>
              {appData?.car_type ? `${appData?.car_type} - ` : ''}
              {appData?.car_subtype ? appData?.car_subtype : ''}
            </span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Марка</span>
            <span className={styles.appDescValue}>{appData?.car_brand}</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Модель</span>
            <span className={styles.appDescValue}>{appData?.vehicle}</span>
          </Box>

          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Количество ТС</span>
            <span className={styles.appDescValue}>{appData?.quantity}</span>
          </Box>

          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Стоимость предмета лизинга</span>
            <span className={styles.appDescValue}>
              {new Intl.NumberFormat('ru-RU').format(appData?.price as number)} ₽
            </span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Первоначальный взнос</span>
            <span className={styles.appDescValue}>{appData?.initial_payment} %</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Срок лизинга</span>
            <span className={styles.appDescValue}>{appData?.lease_period} мес.</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Тип оплаты</span>
            <span className={styles.appDescValue}>{appData?.payment_type}</span>
          </Box>
          {appData?.currency && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Валюта</span>
              <span className={styles.appDescValue}>{appData?.currency}</span>
            </Box>
          )}

          {appData?.inn && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ИНН</span>
              <span className={styles.appDescValue}>{appData?.inn}</span>
            </Box>
          )}

          {appData?.phone && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Телефон</span>
              <span className={styles.appDescValue}>{appData?.phone}</span>
            </Box>
          )}

          {appData?.email && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>E-mail</span>
              <span className={styles.appDescValue}>{appData?.email}</span>
            </Box>
          )}

          {appData?.bio && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ФИО</span>
              <span className={styles.appDescValue}>{appData?.bio}</span>
            </Box>
          )}

          {appData?.kp && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Имеющееся предложение</span>
              <DownloadButton fileUrl={appData?.kp?.url} title={appData?.kp?.name} />
            </Box>
          )}
        </Box>
      );
    }
  };

  const isStock = (status: string) => {
    return status == 'В наличии';
  };
  const isOrder = (status: string) => {
    return status == 'Под заказ';
  };

  const renderHeaderRowSelect = () => {
    return (
      <TableRow className={styles.tableHeadRow}>
        <TableCell
          className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell} ${styles.tableCellSmall}`}
          align='left'
        >
          Транспортное средство
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellSmall}`} align='left'>
          Подтип
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellSmall}`} align='left'>
          Марка
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellSmall}`} align='left'>
          Модель
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableFirstMob}`} align='left'>
          Стоимость
        </TableCell>
        <TableCell className={styles.tableHeadCell} align='left'>
          Наличие
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellMobile}`} align='left'>
          Срок поставки
        </TableCell>
        <TableCell
          className={`${styles.tableHeadCellLast} ${styles.tableHeadCell}`}
          align='right'
        />
      </TableRow>
    );
  };

  const renderHeaderRowLeasing = () => {
    return (
      <TableRow className={styles.tableHeadRow}>
        <TableCell
          className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell} ${styles.tableCellSmall}`}
          align='left'
        >
          Транспортное средство
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellSmall}`} align='left'>
          Подтип
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellSmall}`} align='left'>
          Марка
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellSmall}`} align='left'>
          Модель
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableFirstMob}`} align='left'>
          Стоимость
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellMobile}`} align='left'>
          Первоначальный взнос
        </TableCell>
        <TableCell className={styles.tableHeadCell} align='left'>
          Срок лизинга
        </TableCell>
        <TableCell
          className={`${styles.tableHeadCellLast} ${styles.tableHeadCell}`}
          align='right'
        />
      </TableRow>
    );
  };

  const renderRowsSelect = (rows: any) => {
    const filteredRows = rows.filter((row: any) => row.status_filled === true);
    return filteredRows.map((row: any) => (
      <TableRow
        className={clsx([`${styles.tableBodyRow}`, row.status_accept && `${styles.selectedRow}`])}
        key={row.documentId}
        sx={{ '& td, & th': { border: 0 }, marginTop: '10px' }}
      >
        <TableCell
          className={`${styles.tableBodyCellFirst} ${styles.tableBodyCell}  ${styles.tableCellSmall}`}
          component='th'
          scope='row'
        >
          {row.car_type}
        </TableCell>
        <TableCell
          className={`${styles.tableBodyCell}  ${styles.tableCellSmall}`}
          component='th'
          scope='row'
        >
          {row.car_subtype}
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableCellSmall}`} align='left'>
          {row.car_brand}
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableCellSmall}`} align='left'>
          {row.car_model}
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableFirstMob}`} align='left'>
          {new Intl.NumberFormat('ru-RU').format(row.price as number)} ₽
        </TableCell>
        <TableCell
          className={clsx([
            `${styles.tableBodyCell}`,
            isStock(row.availability) && `${styles.stock}`,
            isOrder(row.availability) && `${styles.order}`,
          ])}
          align='left'
        >
          {row.availability}
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableCellMobile}`} align='left'>
          {row.delivery_time}
        </TableCell>
        <TableCell className={`${styles.tableBodyCellLast} ${styles.tableBodyCell}`} align='right'>
          <IconButton
            disableRipple
            sx={{
              width: '40px',
              height: '40px',
            }}
            onClick={() => navigate(`/dashboard/offer/select/${row.documentId}`)}
            edge='end'
            className={styles.svgIcon}
          >
            <SvgIcon component={ArrowRight} inheritViewBox sx={{ fontSize: '18px' }} />
          </IconButton>
        </TableCell>
      </TableRow>
    ));
  };

  const renderRowsLeasing = (rows: any) => {
    const filteredRows = rows.filter((row: any) => row.status_filled === true);
    return filteredRows.map((row: any) => (
      <TableRow
        className={clsx([`${styles.tableBodyRow}`, row.status_accept && `${styles.selectedRow}`])}
        key={row.documentId}
        sx={{ '& td, & th': { border: 0 }, marginTop: '10px' }}
      >
        <TableCell
          className={`${styles.tableBodyCellFirst} ${styles.tableBodyCell} ${styles.tableCellSmall}`}
          component='th'
          scope='row'
        >
          {row.car_type}/{row.car_subtype}
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableCellSmall}`} align='left'>
          {row.car_brand}
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableCellSmall}`} align='left'>
          {row.vehicle}
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableFirstMob}`} align='left'>
          {new Intl.NumberFormat('ru-RU').format(row.price as number)} ₽
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableCellMobile}`} align='left'>
          {row.initial_payment} %
        </TableCell>
        <TableCell className={styles.tableBodyCell} align='left'>
          {row.lease_period} мес.
        </TableCell>
        <TableCell className={`${styles.tableBodyCellLast} ${styles.tableBodyCell}`} align='right'>
          <IconButton
            disableRipple
            sx={{
              width: '40px',
              height: '40px',
            }}
            onClick={() => navigate(`/dashboard/offer/leasing/${row.documentId}`)}
            edge='end'
            className={styles.svgIcon}
          >
            <SvgIcon component={ArrowRight} inheritViewBox sx={{ fontSize: '18px' }} />
          </IconButton>
        </TableCell>
      </TableRow>
    ));
  };

  const handleCanceled = async () => {
    try {
      if (!appData?.id || !type) return;

      const confirm = window.confirm('Вы уверены, что хотите отменить заявку?');
      if (!confirm) return;

      const data = { id: appData?.id };
      const url = type == 'select' ? '/select-applications/cancel' : '/leasing-applications/cancel';
      await apiClient.post(url, data);
      window.location.reload();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при отправке данных:', error);
    }
  };

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleSaveSuccess = async () => {
    await postData();
  };

  return (
    <Box className={styles.openApp}>
      <DashboardHeaderBackLink link={-1} title={`Заявка № ${appData.id}`} />
      {renderApp()}
      {
        <Box className={styles.buttonContainer}>
          {/* <ButtonCustom
            disabled={['Отменена', 'Закрыта', 'Выполнена'].includes(appData.status_app)}
            className={styles.button}
            onClick={handleCanceled}
          >
            Отменить заявку
          </ButtonCustom> */}
          {appData.fulfilled === false && (
            <ButtonCustom className={styles.button} onClick={handleEdit}>
              Редактировать черновик
            </ButtonCustom>
          )}
        </Box>
      }
      <Box className={styles.offerTableBlock}>
        <span className={styles.offerHeader}>Предложения по заявке</span>
        <Box>
          <TableContainer component={Box} className={styles.tableContainer}>
            <Table className={styles.table} sx={{ minWidth: 1070 }} aria-label={uid}>
              <TableHead className={styles.tableHead}>
                {type == 'select' ? renderHeaderRowSelect() : renderHeaderRowLeasing()}
              </TableHead>
              <TableBody className={styles.tableBody}>
                {type == 'select' ? renderRowsSelect(rowsSelect) : renderRowsLeasing(rowsLeasing)}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* Модальное окно редактирования */}
      <EditApplicationModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        appData={appData}
        type={type}
        onSave={handleSaveSuccess}
        typeValueList={typeValueList}
        subTypeValueList={subTypeValueList}
        brandValueList={brandValueList}
        modelValueList={modelValueList}
        onLoadSubtypes={getSubtypeForType}
        onLoadBrandsForType={getBrandsForType}
        onLoadBrandsForSubtype={getBrandsForSubType}
        onLoadModels={getVehicleForBrandAndSubType}
      />
    </Box>
  );
};
